import { TableColumn } from "@/components/Table/Table";

import { PreviewAvatar } from "./PreviewAvatar";
import {
	CompanyEnrichPreviewExperience,
	CompanyEnrichPreviewPerson,
} from "../schema/companyEnrich";

export const getMatchedExperience = (
	row: CompanyEnrichPreviewPerson
): CompanyEnrichPreviewExperience | null =>
	row.experiences?.find((exp) => exp.is_matched && exp.company?.id && exp.is_current) ?? null;

export const getCurrentExperience = (
	row: CompanyEnrichPreviewPerson
): CompanyEnrichPreviewExperience | null =>
	row.experiences?.find((exp) => exp.is_current && exp.company?.id) ?? null;

const resolveExperience = (row: CompanyEnrichPreviewPerson) =>
	getMatchedExperience(row) ?? getCurrentExperience(row);

/** Build the preview table columns. `ts` is the namespaced translation helper. */
export const getPreviewColumns = (ts: (key: string) => string): TableColumn[] => [
	{
		title: ts("previewName"),
		uid: "name",
		render: (row: CompanyEnrichPreviewPerson) => {
			const nameParts = row.name.trim().split(" ");
			const firstName = nameParts[0] || "";
			const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
			const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

			return (
				<div className="flex items-center gap-2 text-sm">
					<PreviewAvatar imageUrl={row.image_url} initials={initials} name={row.name} />
					{row.name}
				</div>
			);
		},
	},
	{
		title: ts("previewCompany"),
		uid: "company",
		render: (row: CompanyEnrichPreviewPerson) => {
			const company = resolveExperience(row)?.company;

			if (!company) {
				return <div className="text-sm">—</div>;
			}

			return (
				<div className="flex items-center gap-2 text-sm">
					{company.logo_url && (
						<img
							alt={company.name || ""}
							className="w-5 h-5 rounded object-cover"
							src={company.logo_url}
						/>
					)}
					<span className="truncate max-w-[200px]">{company.name || "—"}</span>
				</div>
			);
		},
	},
	{
		title: ts("previewPosition"),
		uid: "position",
		render: (row: CompanyEnrichPreviewPerson) => {
			const matched = resolveExperience(row);

			if (!matched?.company) {
				return <div className="text-sm">—</div>;
			}

			return (
				<div className="text-sm max-w-[300px] truncate">
					{matched?.position || row.position || "—"}
				</div>
			);
		},
	},
	{
		title: ts("previewSeniority"),
		uid: "seniority",
		render: (row: CompanyEnrichPreviewPerson) => (
			<div className="text-sm">{row.seniority || "—"}</div>
		),
	},
	{
		title: ts("previewDepartment"),
		uid: "department",
		render: (row: CompanyEnrichPreviewPerson) => (
			<div className="text-sm">{row.department || "—"}</div>
		),
	},
];
